/**
 * CrmSelect — "What CRM does your company use?" on the signup tab.
 *
 * The client wants this answer on BOTH signup paths, so the login page renders
 * TWO of these: one inside the Google/Apple block and one inside the
 * email/password form. Hence `idPrefix` — without it the two instances would
 * share DOM ids and every label would point at whichever input mounted first,
 * which is the sort of thing that only breaks for screen-reader users.
 *
 * The option list, the stored values, and the validation all live in
 * @/config/crm-options so this component cannot drift from the backend, which
 * validates the exact same way.
 *
 * Value shape mirrors the two columns it feeds (user_profiles.crm_provider /
 * crm_provider_other) rather than collapsing to one string: the picked option is
 * what the client's reporting groups on, and the free text is the escape hatch.
 * Switching away from "Other" clears the free text HERE, in the one place that
 * owns the transition, so no caller can leave an orphaned value behind.
 */

import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CRM_OPTIONS, CRM_OPTION_OTHER, CRM_OTHER_MAX_LENGTH } from '@/config/crm-options';

export interface CrmSelectValue {
  /** A value from CRM_OPTIONS, or '' when nothing has been picked yet. */
  crmProvider: string;
  /** Free text; meaningful only while crmProvider === 'other'. */
  crmProviderOther: string;
}

/** Nothing picked yet — the initial state for both instances on the page. */
export const EMPTY_CRM_SELECTION: CrmSelectValue = {
  crmProvider: '',
  crmProviderOther: '',
};

export interface CrmSelectProps {
  value: CrmSelectValue;
  onChange: (value: CrmSelectValue) => void;
  disabled?: boolean;
  /** Unique per instance — the signup tab mounts this component twice. */
  idPrefix: string;
  /** Inline validation message, rendered under whichever field it belongs to. */
  error?: string;
}

export function CrmSelect({ value, onChange, disabled, idPrefix, error }: CrmSelectProps) {
  const selectId = `${idPrefix}-crm-provider`;
  const otherId = `${idPrefix}-crm-provider-other`;
  const errorId = `${idPrefix}-crm-error`;
  const showOther = value.crmProvider === CRM_OPTION_OTHER;

  return (
    <div className="space-y-2">
      <Label htmlFor={selectId} className="text-sm font-semibold text-gray-700">
        What CRM does your company use?
      </Label>
      <Select
        value={value.crmProvider || undefined}
        onValueChange={(next) =>
          onChange({
            crmProvider: next,
            // Anything other than "Other" drops the free text: it is not stored
            // for a picked option, and keeping it would resurface the moment the
            // user flipped back.
            crmProviderOther: next === CRM_OPTION_OTHER ? value.crmProviderOther : '',
          })
        }
        disabled={disabled}
      >
        <SelectTrigger
          id={selectId}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          className={cn('h-12', error && 'border-red-400')}
        >
          <SelectValue placeholder="Select your CRM" />
        </SelectTrigger>
        <SelectContent className="max-h-72">
          {CRM_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {showOther && (
        <div className="space-y-2 pt-2">
          <Label htmlFor={otherId} className="text-sm font-semibold text-gray-700">
            Other CRM
          </Label>
          <Input
            id={otherId}
            name={otherId}
            type="text"
            value={value.crmProviderOther}
            onChange={(e) =>
              onChange({ crmProvider: value.crmProvider, crmProviderOther: e.target.value })
            }
            placeholder="Enter the CRM you use"
            // Matches the backend cap, so an over-long name is impossible to
            // submit rather than rejected after the fact.
            maxLength={CRM_OTHER_MAX_LENGTH}
            disabled={disabled}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? errorId : undefined}
            className="h-12 bg-white border border-gray-200/50 focus:border-blue-400 focus:ring-blue-400/20 rounded-xl text-base font-medium transition-all duration-200"
          />
        </div>
      )}

      {error && (
        <p id={errorId} className="text-sm text-red-700">
          {error}
        </p>
      )}
    </div>
  );
}

export default CrmSelect;
